// =============================================================================
// ShippingZonesPanel
// =============================================================================
// Platform multi-method shipping configuration: zones (country coverage) and
// the rate methods inside them. Every change fans out a background sync to
// all ACTIVE Shopify stores (their app-owned delivery profile is reconciled
// to this state). The per-store sync status list at the bottom shows
// convergence / last errors.
//
// Method semantics:
//   • name             → checkout rate title (drives billing matching)
//   • amount           → customer-facing Shopify checkout price
//   • wholesaleAmount  → what the merchant is billed per order
//   • externalServiceCode → forwarded to EXTERNAL_API providers (Inkteo
//     requestedService); blank = provider rate-shops
// =============================================================================

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError } from '@tkpod/api-client';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Spinner,
  useConfirm,
} from '@tkpod/ui';

import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ShippingTierRow {
  id: string;
  minValue: string;
  maxValue: string | null;
  amount: string;
  wholesaleAmount: string;
  sortOrder: number;
}

type ShippingRateTypeName = 'FLAT' | 'PACKAGE_WEIGHT' | 'ORDER_AMOUNT';

interface ShippingMethodRow {
  id: string;
  zoneId: string;
  name: string;
  amount: string;
  wholesaleAmount: string;
  currencyCode: string;
  externalServiceCode: string | null;
  rateType: ShippingRateTypeName;
  description: string | null;
  tiers: ShippingTierRow[];
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

interface ShippingZoneRow {
  id: string;
  name: string;
  countries: Array<{ code: string; provinces?: string[] }> | { restOfWorld: true };
  isActive: boolean;
  sortOrder: number;
  methods: ShippingMethodRow[];
}

interface SyncStatusRow {
  connectionId: string;
  name: string;
  shopDomain: string | null;
  setupComplete: boolean;
  lastSyncedAt: string | null;
  lastError: string | null;
}

interface ZoneFormState {
  id: string | null;
  name: string;
  countryCodes: string; // comma-separated ISO-2 codes
  restOfWorld: boolean;
}

interface TierFormRow {
  minValue: string;
  maxValue: string;
  amount: string;
  wholesaleAmount: string;
}

interface MethodFormState {
  id: string | null;
  zoneId: string;
  name: string;
  rateType: ShippingRateTypeName;
  description: string;
  amount: string;
  wholesaleAmount: string;
  currencyCode: string;
  externalServiceCode: string;
  isDefault: boolean;
  tiers: TierFormRow[];
}

const EMPTY_TIER: TierFormRow = {
  minValue: '0',
  maxValue: '',
  amount: '',
  wholesaleAmount: '',
};

function rateTypeLabel(rateType: ShippingRateTypeName): string {
  if (rateType === 'PACKAGE_WEIGHT') return 'Package weight';
  if (rateType === 'ORDER_AMOUNT') return 'Order amount';
  return 'Flat rate';
}

function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    const details =
      err.apiError?.details?.map((detail) => detail.message).filter(Boolean) ??
      [];
    if (details.length > 0) return details.join(' ');
    if (err.message && err.message !== 'Validation failed') return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not save the shipping method.';
}

function parseFormNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function methodFormError(
  form: MethodFormState,
  siblingNames: string[],
): string | null {
  const name = form.name.trim();
  if (name.length < 2) {
    return 'Enter a checkout title of at least 2 characters.';
  }
  const key = name.toLowerCase();
  if (siblingNames.some((sibling) => sibling.trim().toLowerCase() === key)) {
    return 'A method with this checkout title already exists in this zone. Use a different title, or edit the existing method.';
  }
  if (!/^[A-Za-z]{3}$/.test(form.currencyCode.trim())) {
    return 'Currency must be a 3-letter code.';
  }
  if (form.rateType === 'FLAT') {
    if (parseFormNumber(form.amount) == null || parseFormNumber(form.wholesaleAmount) == null) {
      return 'Enter a checkout rate and a wholesale amount.';
    }
    return null;
  }
  if (form.tiers.length === 0) return 'Add at least one tier.';
  for (let index = 0; index < form.tiers.length; index += 1) {
    const tier = form.tiers[index];
    const label = `Tier ${index + 1}`;
    if (parseFormNumber(tier.minValue) == null) return `Enter a minimum for ${label}.`;
    if (parseFormNumber(tier.amount) == null || parseFormNumber(tier.wholesaleAmount) == null) {
      return `Enter a checkout rate and a wholesale amount for ${label}.`;
    }
    const max = tier.maxValue.trim();
    if (max === '' && index !== form.tiers.length - 1) {
      return 'Only the last tier can have an empty maximum.';
    }
    if (max !== '' && parseFormNumber(max) == null) {
      return `Enter a valid maximum for ${label}.`;
    }
  }
  return null;
}

function checkoutSummary(method: ShippingMethodRow): string {
  if (method.rateType === 'FLAT' || method.tiers.length === 0) {
    return `${method.amount} ${method.currencyCode}`;
  }
  const prices = method.tiers.map((tier) => Number.parseFloat(tier.amount));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return `${method.tiers.length} tiers · ${min.toFixed(2)}–${max.toFixed(2)} ${method.currencyCode}`;
}

const ZONES_KEY = ['admin', 'shipping', 'zones'];
const SYNC_KEY = ['admin', 'shipping', 'sync-status'];

function countriesLabel(zone: ShippingZoneRow): string {
  if (!Array.isArray(zone.countries)) return 'Rest of world';
  return zone.countries
    .map((c) =>
      c.provinces && c.provinces.length > 0
        ? `${c.code} (${c.provinces.length} provinces)`
        : c.code,
    )
    .join(', ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShippingZonesPanel() {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const [feedback, setFeedback] = useState<string | null>(null);
  const [zoneForm, setZoneForm] = useState<ZoneFormState | null>(null);
  const [methodForm, setMethodForm] = useState<MethodFormState | null>(null);

  const zonesQuery = useQuery<{ data: ShippingZoneRow[] }, Error>({
    queryKey: ZONES_KEY,
    queryFn: () => api.get<{ data: ShippingZoneRow[] }>('/api/v1/admin/shipping/zones'),
  });

  const syncQuery = useQuery<{ data: SyncStatusRow[] }, Error>({
    queryKey: SYNC_KEY,
    queryFn: () => api.get<{ data: SyncStatusRow[] }>('/api/v1/admin/shipping/sync-status'),
    refetchInterval: 30_000,
  });

  const invalidate = async () => {
    setFeedback(null);
    await queryClient.invalidateQueries({ queryKey: ZONES_KEY });
    await queryClient.invalidateQueries({ queryKey: SYNC_KEY });
  };

  const onError = (err: unknown) => setFeedback(saveErrorMessage(err));

  // Openers clear any stale error from a previous attempt; save errors are
  // rendered inside the open modal (the card-level banner sits behind it).
  const openZoneForm = (form: ZoneFormState) => {
    setFeedback(null);
    setZoneForm(form);
  };
  const openMethodForm = (form: MethodFormState) => {
    setFeedback(null);
    setMethodForm(form);
  };

  // ---- Mutations -----------------------------------------------------------

  const saveZoneMut = useMutation({
    mutationFn: (form: ZoneFormState) => {
      const body: Record<string, unknown> = { name: form.name.trim() };
      if (form.restOfWorld) {
        body.restOfWorld = true;
      } else {
        body.countries = form.countryCodes
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean)
          .map((code) => ({ code }));
      }
      return form.id
        ? api.patch(`/api/v1/admin/shipping/zones/${form.id}`, body)
        : api.post('/api/v1/admin/shipping/zones', body);
    },
    onSuccess: async () => {
      setZoneForm(null);
      await invalidate();
    },
    onError,
  });

  const toggleZoneMut = useMutation({
    mutationFn: (zone: ShippingZoneRow) =>
      api.patch(`/api/v1/admin/shipping/zones/${zone.id}`, {
        isActive: !zone.isActive,
      }),
    onSuccess: invalidate,
    onError,
  });

  const deleteZoneMut = useMutation({
    mutationFn: (zoneId: string) =>
      api.delete(`/api/v1/admin/shipping/zones/${zoneId}`),
    onSuccess: invalidate,
    onError,
  });

  const saveMethodMut = useMutation({
    mutationFn: (form: MethodFormState) => {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        rateType: form.rateType,
        description: form.description.trim(),
        currencyCode: form.currencyCode.trim().toUpperCase(),
        externalServiceCode: form.externalServiceCode.trim(),
        isDefault: form.isDefault,
      };
      if (form.rateType === 'FLAT') {
        body.amount = Number.parseFloat(form.amount);
        body.wholesaleAmount = Number.parseFloat(form.wholesaleAmount);
        body.tiers = [];
      } else {
        body.tiers = form.tiers.map((tier) => ({
          minValue: Number.parseFloat(tier.minValue),
          maxValue: tier.maxValue.trim() === '' ? null : Number.parseFloat(tier.maxValue),
          amount: Number.parseFloat(tier.amount),
          wholesaleAmount: Number.parseFloat(tier.wholesaleAmount),
        }));
      }
      return form.id
        ? api.patch(`/api/v1/admin/shipping/methods/${form.id}`, body)
        : api.post(`/api/v1/admin/shipping/zones/${form.zoneId}/methods`, body);
    },
    onSuccess: async () => {
      setMethodForm(null);
      await invalidate();
    },
    onError,
  });

  const toggleMethodMut = useMutation({
    mutationFn: (method: ShippingMethodRow) =>
      api.patch(`/api/v1/admin/shipping/methods/${method.id}`, {
        isActive: !method.isActive,
      }),
    onSuccess: invalidate,
    onError,
  });

  const setDefaultMut = useMutation({
    mutationFn: (methodId: string) =>
      api.patch(`/api/v1/admin/shipping/methods/${methodId}`, {
        isDefault: true,
      }),
    onSuccess: invalidate,
    onError,
  });

  const deleteMethodMut = useMutation({
    mutationFn: (methodId: string) =>
      api.delete(`/api/v1/admin/shipping/methods/${methodId}`),
    onSuccess: invalidate,
    onError,
  });

  const syncNowMut = useMutation({
    mutationFn: () => api.post('/api/v1/admin/shipping/sync', {}),
    onSuccess: async () => {
      setFeedback(null);
      await queryClient.invalidateQueries({ queryKey: SYNC_KEY });
    },
    onError,
  });

  // ---- Handlers ------------------------------------------------------------

  const handleDeleteZone = async (zone: ShippingZoneRow) => {
    const ok = await confirm({
      title: 'Delete shipping zone',
      description:
        `"${zone.name}" and its ${zone.methods.length} method(s) will be removed ` +
        'from all Shopify stores on the next sync. This cannot be undone.',
      confirmLabel: 'Delete zone',
      tone: 'danger',
    });
    if (ok) deleteZoneMut.mutate(zone.id);
  };

  const handleDeleteMethod = async (method: ShippingMethodRow) => {
    const ok = await confirm({
      title: 'Delete shipping method',
      description:
        `"${method.name}" will be removed from checkout in all Shopify stores ` +
        'on the next sync. This cannot be undone.',
      confirmLabel: 'Delete method',
      tone: 'danger',
    });
    if (ok) deleteMethodMut.mutate(method.id);
  };

  const submitZoneForm = (e: FormEvent) => {
    e.preventDefault();
    if (zoneForm) saveZoneMut.mutate(zoneForm);
  };

  const submitMethodForm = (e: FormEvent) => {
    e.preventDefault();
    if (!methodForm) return;
    const zone = (zonesQuery.data?.data ?? []).find((row) => row.id === methodForm.zoneId);
    const siblingNames = (zone?.methods ?? [])
      .filter((method) => method.id !== methodForm.id)
      .map((method) => method.name);
    const message = methodFormError(methodForm, siblingNames);
    if (message) {
      setFeedback(message);
      return;
    }
    setFeedback(null);
    saveMethodMut.mutate(methodForm);
  };

  // ---- Render --------------------------------------------------------------

  if (zonesQuery.isLoading) {
    return (
      <Card>
        <CardHeader
          title="Shipping zones & methods"
          description="Platform-wide checkout rates synced to every Shopify store."
        />
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (zonesQuery.isError) {
    return (
      <Card>
        <CardHeader
          title="Shipping zones & methods"
          description="Platform-wide checkout rates synced to every Shopify store."
        />
        <div className="mt-4 rounded-md bg-error-muted p-4 text-sm text-error">
          Failed to load: {zonesQuery.error.message}
        </div>
      </Card>
    );
  }

  const zones = zonesQuery.data?.data ?? [];
  const syncRows = syncQuery.data?.data ?? [];
  const isMutating =
    saveZoneMut.isPending ||
    toggleZoneMut.isPending ||
    deleteZoneMut.isPending ||
    saveMethodMut.isPending ||
    toggleMethodMut.isPending ||
    setDefaultMut.isPending ||
    deleteMethodMut.isPending;

  return (
    <div className="space-y-6">
      {dialog}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <CardHeader
            title="Shipping zones & methods"
            description="Multiple rates per zone, one default. Changes sync automatically to every active Shopify store's TK delivery profile and drive per-order wholesale shipping billing."
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => syncNowMut.mutate()}
              disabled={syncNowMut.isPending}
            >
              {syncNowMut.isPending ? 'Queuing…' : 'Sync all stores'}
            </Button>
            <Button
              type="button"
              onClick={() =>
                openZoneForm({
                  id: null,
                  name: '',
                  countryCodes: '',
                  restOfWorld: false,
                })
              }
            >
              Add zone
            </Button>
          </div>
        </div>

        {feedback ? (
          <div className="mt-4 rounded-md bg-error-muted p-3 text-sm text-error">
            {feedback}
          </div>
        ) : null}

        <div className="mt-6 space-y-6">
          {zones.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-tertiary">
              No shipping zones configured yet.
            </p>
          ) : null}

          {zones.map((zone) => (
            <div
              key={zone.id}
              className="rounded-lg border border-border-subtle"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle bg-surface-subtle px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-text-primary">
                    {zone.name}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {countriesLabel(zone)}
                  </span>
                  <Badge
                    variant={zone.isActive ? 'success' : 'warning'}
                    size="sm"
                  >
                    {zone.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() =>
                      openMethodForm({
                        id: null,
                        zoneId: zone.id,
                        name: '',
                        rateType: 'FLAT',
                        description: '',
                        amount: '',
                        wholesaleAmount: '',
                        currencyCode: 'USD',
                        externalServiceCode: '',
                        isDefault: zone.methods.length === 0,
                        tiers: [],
                      })
                    }
                  >
                    Add method
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() =>
                      openZoneForm({
                        id: zone.id,
                        name: zone.name,
                        countryCodes: Array.isArray(zone.countries)
                          ? zone.countries.map((c) => c.code).join(', ')
                          : '',
                        restOfWorld: !Array.isArray(zone.countries),
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => toggleZoneMut.mutate(zone)}
                  >
                    {zone.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => void handleDeleteZone(zone)}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs uppercase text-text-tertiary">
                    <tr>
                      <th className="px-4 py-2 text-left">Default</th>
                      <th className="px-4 py-2 text-left">Method</th>
                      <th className="px-4 py-2 text-left">Rate type</th>
                      <th className="px-4 py-2 text-right">Checkout rate</th>
                      <th className="px-4 py-2 text-right">Wholesale</th>
                      <th className="px-4 py-2 text-left">Service code</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zone.methods.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-4 text-center text-xs text-text-tertiary"
                        >
                          No methods yet — the zone will not sync until it has
                          an active method.
                        </td>
                      </tr>
                    ) : null}
                    {zone.methods.map((m) => (
                      <tr key={m.id} className="border-t border-border-subtle">
                        <td className="px-4 py-2">
                          <input
                            type="radio"
                            name={`default-${zone.id}`}
                            checked={m.isDefault}
                            disabled={isMutating || m.isDefault}
                            onChange={() => setDefaultMut.mutate(m.id)}
                            aria-label={`Set ${m.name} as default`}
                          />
                        </td>
                        <td className="px-4 py-2 font-medium text-text-primary">
                          {m.name}
                        </td>
                        <td className="px-4 py-2 text-xs text-text-secondary">
                          {rateTypeLabel(m.rateType ?? 'FLAT')}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {checkoutSummary({ ...m, rateType: m.rateType ?? 'FLAT', tiers: m.tiers ?? [] })}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {m.rateType && m.rateType !== 'FLAT'
                            ? 'Per tier'
                            : `${m.wholesaleAmount} ${m.currencyCode}`}
                        </td>
                        <td className="px-4 py-2 text-xs text-text-secondary">
                          {m.externalServiceCode ?? '—'}
                        </td>
                        <td className="px-4 py-2">
                          <Badge
                            variant={m.isActive ? 'success' : 'warning'}
                            size="sm"
                          >
                            {m.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isMutating}
                              onClick={() =>
                                openMethodForm({
                                  id: m.id,
                                  zoneId: zone.id,
                                  name: m.name,
                                  rateType: m.rateType ?? 'FLAT',
                                  description: m.description ?? '',
                                  amount: m.amount,
                                  wholesaleAmount: m.wholesaleAmount,
                                  currencyCode: m.currencyCode,
                                  externalServiceCode:
                                    m.externalServiceCode ?? '',
                                  isDefault: m.isDefault,
                                  tiers: (m.tiers ?? []).map((tier) => ({
                                    minValue: tier.minValue,
                                    maxValue: tier.maxValue ?? '',
                                    amount: tier.amount,
                                    wholesaleAmount: tier.wholesaleAmount,
                                  })),
                                })
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isMutating}
                              onClick={() => toggleMethodMut.mutate(m)}
                            >
                              {m.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isMutating}
                              onClick={() => void handleDeleteMethod(m)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Store sync status"
          description="Last shipping-config sync per active Shopify store. Failures retry automatically with backoff; use 'Sync all stores' to re-run immediately."
        />
        <div className="mt-4 overflow-x-auto rounded-md border border-border-subtle">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-subtle text-xs uppercase text-text-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">Store</th>
                <th className="px-3 py-2 text-left">Setup</th>
                <th className="px-3 py-2 text-left">Last synced</th>
                <th className="px-3 py-2 text-left">Last error</th>
              </tr>
            </thead>
            <tbody>
              {syncRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-4 text-center text-xs text-text-tertiary"
                  >
                    No active Shopify stores.
                  </td>
                </tr>
              ) : null}
              {syncRows.map((row) => (
                <tr
                  key={row.connectionId}
                  className="border-t border-border-subtle"
                >
                  <td className="px-3 py-2">
                    <span className="font-medium text-text-primary">
                      {row.name}
                    </span>
                    {row.shopDomain ? (
                      <span className="ml-2 text-xs text-text-tertiary">
                        {row.shopDomain}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={row.setupComplete ? 'success' : 'warning'}
                      size="sm"
                    >
                      {row.setupComplete ? 'Ready' : 'Pending setup'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {row.lastSyncedAt
                      ? new Date(row.lastSyncedAt).toLocaleString()
                      : 'Never'}
                  </td>
                  <td className="px-3 py-2 text-xs text-error">
                    {row.lastError ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Zone modal ---- */}
      <Modal
        open={zoneForm !== null}
        onClose={() => setZoneForm(null)}
        title={zoneForm?.id ? 'Edit zone' : 'Add zone'}
      >
        {zoneForm ? (
          <form onSubmit={submitZoneForm} className="space-y-4">
            <Input
              label="Zone name"
              value={zoneForm.name}
              onChange={(e) =>
                setZoneForm({ ...zoneForm, name: e.target.value })
              }
              required
              minLength={2}
              maxLength={100}
            />
            <label className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={zoneForm.restOfWorld}
                onChange={(e) =>
                  setZoneForm({ ...zoneForm, restOfWorld: e.target.checked })
                }
              />
              Rest of world (catch-all zone)
            </label>
            {!zoneForm.restOfWorld ? (
              <Input
                label="Country codes (ISO-2, comma separated)"
                value={zoneForm.countryCodes}
                onChange={(e) =>
                  setZoneForm({ ...zoneForm, countryCodes: e.target.value })
                }
                placeholder="US, CA, MX"
                required
              />
            ) : null}
            {feedback ? (
              <div className="rounded-md bg-error-muted p-3 text-sm text-error">
                {feedback}
              </div>
            ) : null}
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setZoneForm(null)}
                disabled={saveZoneMut.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saveZoneMut.isPending}>
                {saveZoneMut.isPending ? 'Saving…' : 'Save zone'}
              </Button>
            </ModalFooter>
          </form>
        ) : null}
      </Modal>

      {/* ---- Method modal ---- */}
      <Modal
        open={methodForm !== null}
        onClose={() => setMethodForm(null)}
        title={methodForm?.id ? 'Edit method' : 'Add method'}
        size="lg"
      >
        {methodForm ? (
          <form onSubmit={submitMethodForm} className="space-y-4">
            {feedback ? (
              <div className="rounded-md bg-error-muted p-3 text-sm text-error">
                {feedback}
              </div>
            ) : null}
            <Input
              label="Method name (checkout rate title)"
              value={methodForm.name}
              onChange={(e) =>
                setMethodForm({ ...methodForm, name: e.target.value })
              }
              required
              minLength={2}
              maxLength={100}
            />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-text-secondary">
                Rate type
              </span>
              <select
                className="h-9 w-full rounded-md border border-border-subtle bg-surface px-2 text-sm text-text-primary"
                value={methodForm.rateType}
                onChange={(e) => {
                  const rateType = e.target.value as ShippingRateTypeName;
                  setMethodForm({
                    ...methodForm,
                    rateType,
                    tiers:
                      rateType === 'FLAT'
                        ? []
                        : methodForm.tiers.length > 0
                          ? methodForm.tiers
                          : [{ ...EMPTY_TIER }],
                  });
                }}
              >
                <option value="FLAT">Flat rate</option>
                <option value="PACKAGE_WEIGHT">Package weight</option>
                <option value="ORDER_AMOUNT">Order amount</option>
              </select>
            </label>
            {methodForm.rateType === 'FLAT' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Checkout rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={methodForm.amount}
                  onChange={(e) =>
                    setMethodForm({ ...methodForm, amount: e.target.value })
                  }
                  required
                />
                <Input
                  label="Wholesale (billed)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={methodForm.wholesaleAmount}
                  onChange={(e) =>
                    setMethodForm({
                      ...methodForm,
                      wholesaleAmount: e.target.value,
                    })
                  }
                  required
                />
                <Input
                  label="Currency"
                  value={methodForm.currencyCode}
                  onChange={(e) =>
                    setMethodForm({
                      ...methodForm,
                      currencyCode: e.target.value,
                    })
                  }
                  required
                  maxLength={3}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Currency"
                    value={methodForm.currencyCode}
                    onChange={(e) =>
                      setMethodForm({
                        ...methodForm,
                        currencyCode: e.target.value,
                      })
                    }
                    required
                    maxLength={3}
                  />
                </div>
                <p className="text-sm text-text-secondary">
                  {methodForm.rateType === 'PACKAGE_WEIGHT'
                    ? 'Weight is in pounds. Start at 0, leave no gaps, and leave the last Max empty.'
                    : 'Amount is the merchandise total. Start at 0, leave no gaps, and leave the last Max empty.'}
                  {' '}A different checkout title is a separate option. The same title cannot be added twice.
                </p>
                <div className="overflow-x-auto rounded-md border border-border-subtle">
                  <div className="grid min-w-[36rem] grid-cols-[minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_auto] gap-2 px-3 py-2 text-xs font-medium text-text-secondary">
                    <span>Min</span>
                    <span>Max</span>
                    <span>Checkout</span>
                    <span>Wholesale</span>
                    <span className="sr-only">Remove</span>
                  </div>
                {methodForm.tiers.map((tier, index) => (
                  <div
                    key={`tier-${index}`}
                    className="grid min-w-[36rem] grid-cols-[minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_auto] items-center gap-2 border-t border-border-subtle px-3 py-2"
                  >
                    <Input
                      id={`tier-${index}-min`}
                      aria-label={`Tier ${index + 1} minimum`}
                      type="number"
                      step="0.0001"
                      min="0"
                      value={tier.minValue}
                      onChange={(e) => {
                        const tiers = methodForm.tiers.slice();
                        tiers[index] = { ...tier, minValue: e.target.value };
                        setMethodForm({ ...methodForm, tiers });
                      }}
                      required
                    />
                    <Input
                      id={`tier-${index}-max`}
                      aria-label={`Tier ${index + 1} maximum, empty means no limit`}
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder="No limit"
                      value={tier.maxValue}
                      onChange={(e) => {
                        const tiers = methodForm.tiers.slice();
                        tiers[index] = { ...tier, maxValue: e.target.value };
                        setMethodForm({ ...methodForm, tiers });
                      }}
                    />
                    <Input
                      id={`tier-${index}-checkout`}
                      aria-label={`Tier ${index + 1} checkout rate`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={tier.amount}
                      onChange={(e) => {
                        const tiers = methodForm.tiers.slice();
                        tiers[index] = { ...tier, amount: e.target.value };
                        setMethodForm({ ...methodForm, tiers });
                      }}
                      required
                    />
                    <Input
                      id={`tier-${index}-wholesale`}
                      aria-label={`Tier ${index + 1} wholesale amount`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={tier.wholesaleAmount}
                      onChange={(e) => {
                        const tiers = methodForm.tiers.slice();
                        tiers[index] = {
                          ...tier,
                          wholesaleAmount: e.target.value,
                        };
                        setMethodForm({ ...methodForm, tiers });
                      }}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={methodForm.tiers.length <= 1}
                      onClick={() =>
                        setMethodForm({
                          ...methodForm,
                          tiers: methodForm.tiers.filter((_, i) => i !== index),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={methodForm.tiers.length >= 40}
                  onClick={() =>
                    setMethodForm({
                      ...methodForm,
                      tiers: [
                        ...methodForm.tiers,
                        {
                          minValue:
                            methodForm.tiers[methodForm.tiers.length - 1]?.maxValue ||
                            '',
                          maxValue: '',
                          amount: '',
                          wholesaleAmount: '',
                        },
                      ],
                    })
                  }
                >
                  Add tier
                </Button>
              </div>
            )}
            <Input
              label="Delivery details (optional)"
              value={methodForm.description}
              onChange={(e) =>
                setMethodForm({
                  ...methodForm,
                  description: e.target.value,
                })
              }
              maxLength={255}
              placeholder="Shown under the rate at checkout"
            />
            <Input
              label="External service code (optional)"
              value={methodForm.externalServiceCode}
              onChange={(e) =>
                setMethodForm({
                  ...methodForm,
                  externalServiceCode: e.target.value,
                })
              }
              placeholder="e.g. usps_ground_advantage"
              maxLength={100}
            />
            <p className="text-[11px] text-text-tertiary">
              Sent to external fulfillment providers (Inkteo requestedService)
              when an order used this method at checkout. Leave blank to let
              the provider pick the cheapest service.
            </p>
            <label className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={methodForm.isDefault}
                onChange={(e) =>
                  setMethodForm({
                    ...methodForm,
                    isDefault: e.target.checked,
                  })
                }
                disabled={methodForm.id !== null && methodForm.isDefault}
              />
              Default method (billing fallback when the checkout title does
              not match any method)
            </label>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMethodForm(null)}
                disabled={saveMethodMut.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saveMethodMut.isPending}>
                {saveMethodMut.isPending ? 'Saving…' : 'Save method'}
              </Button>
            </ModalFooter>
          </form>
        ) : null}
      </Modal>
    </div>
  );
}
